import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import type { ArtifactPreview } from "~/services/artifacts";

function imageUrl(content: ArtifactPreview["content"]): string | null {
  if (!content || typeof content !== "object" || Array.isArray(content)) return null;
  const url = (content as Record<string, unknown>).url;
  return typeof url === "string" ? url : null;
}

// Below this threshold the floating chip dwarfs the thumbnail, so we hide
// it inline and rely on the lightbox's labeled Download button instead.
const DOWNLOAD_CHIP_MIN_SIZE = 100;

export const ImageArtifact = ({ preview }: { preview: ArtifactPreview }) => {
  const src = imageUrl(preview.content);
  const [showDownloadChip, setShowDownloadChip] = useState(false);

  if (!src) {
    return <div className="my-3 text-sm text-destructive">Unexpected image preview shape.</div>;
  }

  const title = preview.artifact_name ?? preview.filename ?? "Image";
  const altText = preview.summary ?? preview.filename ?? preview.artifact_name ?? "Image artifact";
  const downloadName = preview.filename ?? "";

  return (
    <Dialog>
      <div className="relative my-3 inline-block">
        <DialogTrigger asChild>
          <button
            type="button"
            className="block cursor-zoom-in overflow-hidden rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Open ${title}`}
          >
            <img
              src={src}
              alt={altText}
              className="max-h-96 w-auto object-contain"
              onLoad={(e) => {
                const img = e.currentTarget;
                setShowDownloadChip(
                  img.naturalWidth >= DOWNLOAD_CHIP_MIN_SIZE &&
                    img.naturalHeight >= DOWNLOAD_CHIP_MIN_SIZE
                );
              }}
            />
          </button>
        </DialogTrigger>

        {showDownloadChip && (
          <a
            href={src}
            download={downloadName}
            className="absolute bottom-2 right-2 flex size-7 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Download image"
          >
            <Download className="size-3.5" />
          </a>
        )}
      </div>

      <DialogContent className="flex w-fit min-w-[320px] max-w-[95vw] flex-col gap-3 sm:max-w-[95vw]">
        <DialogTitle className="truncate pr-10 text-sm font-medium">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          {preview.summary ?? `Full-size view of ${title}`}
        </DialogDescription>
        <div className="relative flex items-center justify-center rounded bg-muted/40">
          <img src={src} alt={altText} className="max-h-[80vh] max-w-full object-contain" />
          {showDownloadChip && (
            <a
              href={src}
              download={downloadName}
              className="absolute bottom-2 right-2 flex size-9 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Download image"
            >
              <Download className="size-4" />
            </a>
          )}
        </div>
        {preview.summary && (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{preview.summary}</p>
        )}
        {!showDownloadChip && (
          <div className="flex justify-end">
            <Button asChild variant="outline" size="sm" aria-label="Download image">
              <a href={src} download={downloadName}>
                <Download className="size-3.5" />
                Download
              </a>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
